import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { completeOfflineRegistration, completePayment, quotePayment } from '../actions/programRegistrationActions'
import { markSetupConfirmed, resetPaymentSession, setIsProcessingPayment, setPaymentError } from '../reducers/programRegistrationReducer'
import { getConnectedAccountStripe } from '../utility/stripe'

const useCardPaymentFlow = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const currentUser = useSelector(state => state.user.current_user)
  const paymentSession = useSelector(state => state.programRegistration.paymentSession)
  const quote = paymentSession?.quote

  const failPayment = (message, { restart }) => {
    dispatch(setPaymentError(message || t('programs.registration.checkoutStep.paymentFailed')))
    if (restart) {
      dispatch(resetPaymentSession())
    }
  }

  // Saves the card on the platform account, no money moves at this step
  const confirmCardSetup = async () => {
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: { billing_details: { email: currentUser.email } },
      },
      redirect: 'if_required',
    })

    // a confirm that already went through (double click, lost response) comes back as an error carrying the succeeded SetupIntent
    const confirmedSetupIntent = setupIntent || error?.setup_intent

    if (confirmedSetupIntent?.status !== 'succeeded') {
      failPayment(error?.message, { restart: false })
      return false
    }

    dispatch(markSetupConfirmed())
    return true
  }

  const completeRegistrationPayment = async () => {
    const feeLine = quote.pricing.lines.find(line => line.key === 'program_fee')
    const completion = feeLine?.paymentMethod === 'card' ? completePayment : completeOfflineRegistration

    try {
      return await dispatch(completion({ registrationId: paymentSession.registrationId })).unwrap()
    } catch (rejection) {
      if (rejection?.quoteRequired) {
        // the quote the payer agreed to is gone, so the breakdown, the summary and the Pay label refresh before the next attempt
        failPayment(rejection.quoteRequired[0], { restart: false })
        dispatch(quotePayment({ registrationId: paymentSession.registrationId, ...quote.params }))
        return null
      }

      const message = rejection?.cardDeclined?.[0] || rejection?.notPayable?.[0] || rejection?.cardNotConfirmed?.[0] ||
        rejection?.membershipChargeFailed?.[0] || rejection?.membershipSeatsExhausted?.[0]
      failPayment(message, { restart: Boolean(message) })
      return null
    }
  }

  // the fee PaymentIntent lives on the connected account and the membership invoice on the platform,
  // so each challenge needs the matching Stripe instance
  const authenticatePayment = async ({ stripeAccountId, paymentIntentClientSecret }) => {
    const stripeInstance = stripeAccountId ? await getConnectedAccountStripe(stripeAccountId) : stripe
    const { error } = await stripeInstance.handleNextAction({ clientSecret: paymentIntentClientSecret })

    if (error) {
      failPayment(error.message, { restart: true })
      return false
    }

    return true
  }

  // Answers one 3DS challenge and asks the server to complete again; a result that needs no
  // challenge passes straight through, so the two calls below cover a fee-only registration too
  const answerChallenge = async (result) => {
    if (result?.status !== 'requires_action') {
      return result
    }

    return (await authenticatePayment(result)) ? await completeRegistrationPayment() : null
  }

  return async () => {
    if (!stripe || !elements || paymentSession?.status !== 'ready' || quote?.status !== 'ready') {
      return null
    }

    dispatch(setIsProcessingPayment(true))
    dispatch(setPaymentError(null))

    try {
      if (quote.cardRequired && !paymentSession.setupConfirmed && !(await confirmCardSetup())) {
        return null
      }

      let result = await completeRegistrationPayment()

      // the program fee's challenge comes first, on the community's connected account
      result = await answerChallenge(result)
      // then the membership invoice's, on the platform account
      result = await answerChallenge(result)

      if (result?.status === 'requires_action') {
        failPayment(null, { restart: true })
        return null
      }

      if (result?.status === 'succeeded') {
        return paymentSession.registrationId
      }

      if (result) {
        throw new Error(`Unexpected complete_payment status: ${result.status}`)
      }

      return null
    } finally {
      dispatch(setIsProcessingPayment(false))
    }
  }
}

export default useCardPaymentFlow
